import * as React from 'react';
import { AYRILMIS_SLUGLAR, PLANLAR, PLAN_OZELLIKLERI } from '@hotelcms/shared';
import { Kart, KartBaslik, KartGovde, Rozet } from '@/components/ui/card';

/**
 * Kurulum ve dokumantasyon.
 * Yeni tesis acan kisinin ve nobetteki destegin ihtiyaci olan her sey burada;
 * yetkilendirme gerektirir, herkese acik degildir.
 */
export function Kurulum({ platformDomain }: { platformDomain: string }) {
  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-5 p-4 sm:p-6">
      <header>
        <h1 className="text-2xl font-bold">Kurulum ve dokümantasyon</h1>
        <p className="text-slate-600">Platformu işletmek için gereken adımlar ve sık karşılaşılan durumlar.</p>
      </header>

      <Bolum baslik="1. Yeni tesis nasıl açılır?" rozet="Günlük iş">
        <ol className="ml-5 list-decimal space-y-2">
          <li>
            <strong>Tesisler → + Yeni tesis</strong> ekranını açın.
          </li>
          <li>
            Tesis adını yazın; internet adresi otomatik önerilir (<code>Ala Otel Akyaka → alaotel</code>).
            Yeşil "uygun" yazısını görmeden devam etmeyin.
          </li>
          <li>Planı seçin. Oda sayısı plan limitini aşarsa form gönderilmez.</li>
          <li>
            Yetkili adı ve e-postasını girin — bu hesap <strong>OWNER</strong> yetkisiyle açılır.
          </li>
          <li>
            Oda tiplerini ve adetlerini girin. Gecelik fiyat yazarsanız 120 günlük fiyat takvimi hazır gelir.
          </li>
          <li>
            KBS bilgileri varsa şimdi girin, yoksa "Kapalı" bırakın; tesis sonradan Ayarlar'dan tanımlayabilir.
          </li>
          <li>
            <strong>Tesisi oluştur</strong> → panel adresi ve geçici parola ekranda çıkar.
          </li>
        </ol>
        <Uyari>
          Geçici parola <strong>yalnızca bir kez</strong> gösterilir ve hiçbir yerde saklanmaz. Kapatmadan önce
          tesise iletin. Kaybolursa tesis satırındaki <strong>Parola</strong> düğmesiyle yenisini üretin.
        </Uyari>
      </Bolum>

      <Bolum baslik="2. Adres kuralları" rozet="Alt alan adı">
        <p>
          Her tesis <code>{'<adres>'}.{platformDomain}</code> üzerinden açılır. Adres 3–30 karakter, küçük harf,
          rakam ve tek tire içerebilir; tire ile başlayıp bitemez.
        </p>
        <p className="mt-3">
          Aşağıdaki adresler platform tarafından ayrılmıştır ve tesise verilemez:
        </p>
        <div className="mt-2 flex flex-wrap gap-1">
          {[...AYRILMIS_SLUGLAR].slice(0, 28).map((s) => (
            <code key={s} className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">
              {s}
            </code>
          ))}
          <span className="text-xs text-slate-500">… ve diğerleri</span>
        </div>
        <Uyari renk="bilgi">
          Adres sonradan değiştirilemez (tesisin kayıtlı bağlantıları bozulur). Yanlış açılırsa tesisi iptal edip
          yeniden oluşturun.
        </Uyari>
      </Bolum>

      <Bolum baslik="3. DNS ve alan adı" rozet="Kurulu">
        <p>
          Bölgede <strong>joker DNS kaydı</strong> tanımlıdır ve <code>{'*.'}{platformDomain}{'/*'}</code> rotası
          yapılandırmanın parçasıdır. Birlikte şu anlama gelir:
        </p>
        <ul className="ml-5 mt-3 list-disc space-y-2">
          <li>Yeni tesis kurulunca adresi <strong>anında</strong> açılır, dış bir API çağrısı yapılmaz.</li>
          <li>Kod dağıtımları tesis adreslerini <strong>etkilemez</strong>.</li>
          <li>Tanımsız bir alt alan adresine girilirse Türkçe "Tesis bulunamadı" sayfası döner.</li>
        </ul>
        <Kod>{`A    *.${platformDomain}    ->    192.0.2.1    (Proxied)`}</Kod>
        <p className="mt-3">
          Apex (<code>{platformDomain}</code>) ve <code>www</code> ayrıca Worker özel alan adı olarak
          tanımlıdır. Universal SSL yalnızca <strong>tek seviyeyi</strong> kapsar:
          <code> a.b.{platformDomain}</code> gibi iki seviyeli adresler bilinçli olarak reddedilir.
        </p>
      </Bolum>

      <Bolum baslik="4. Planlar ve limitler">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="p-2">Plan</th>
                <th className="p-2">Oda limiti</th>
                <th className="p-2">Günlük kimlik okuma</th>
                <th className="p-2">Açıklama</th>
              </tr>
            </thead>
            <tbody>
              {PLANLAR.map((p) => (
                <tr key={p} className="border-t border-slate-100">
                  <td className="p-2 font-semibold">{p}</td>
                  <td className="p-2">{PLAN_OZELLIKLERI[p].odaLimiti}</td>
                  <td className="p-2">{PLAN_OZELLIKLERI[p].ocrGunlukKota}</td>
                  <td className="p-2 text-slate-600">{PLAN_OZELLIKLERI[p].aciklama}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-slate-600">
          Plan değiştirdiğinizde oda limiti ve kimlik okuma kotası plan varsayılanına çekilir; elle değer
          girerseniz o değer korunur. DENEME planı 14 gün sonra biter — bitiş tarihi tesis satırında görünür.
        </p>
      </Bolum>

      <Bolum baslik="5. Tesis durumları">
        <ul className="ml-5 list-disc space-y-2">
          <li>
            <strong>Deneme / Aktif</strong> — panel normal çalışır.
          </li>
          <li>
            <strong>Askıda</strong> — panel açılmaz, kullanıcılar "hesabınız askıya alınmış" sayfasını görür.
            Veriler <em>silinmez</em>. Ödeme gecikmesinde kullanın.
          </li>
          <li>
            <strong>İptal</strong> — hesap kapatılmış sayılır. Veriler durur ama erişim yoktur.
          </li>
        </ul>
        <Uyari renk="bilgi">
          Durum değişikliği anında etkilidir: adres önbelleği yazma işleminden sonra düşürülür.
        </Uyari>
      </Bolum>

      <Bolum baslik="6. KBS entegrasyonu" rozet="Resmi servis">
        <p>
          Resmi servis: <code>vatandas.jandarma.gov.tr/KBS_Tesis_Servis/SrvShsYtkTml.svc</code> — SOAP 1.1
          tabanlı XML web servisi. Kaynak: <em>KBS Tesis Web Servisleri Bilgi Dokümanı</em> (JGNK, Sürüm 2).
        </p>
        <p className="mt-3">Her çağrı üç güvenlik parametresi ister:</p>
        <ul className="ml-5 mt-2 list-disc space-y-1">
          <li><strong>TssKod</strong> — JGNK'nın tesise verdiği kod, bağlı karakol komutanlığından öğrenilir</li>
          <li><strong>KullaniciTC</strong> — servisi çağıracak yetkilinin kimlik numarası</li>
          <li><strong>Sifre</strong> — kişiye özel web servis şifresi (karakol komutanlığı tanımlar)</li>
        </ul>

        <Uyari renk="bilgi">
          <strong>Sabit IP aktarıcısı kurulu.</strong> KBS, çağrıyı yapan IP adresinin kendi arayüzünde kayıtlı
          olmasını şart koşar; Cloudflare Workers'ın çıkış IP'si sabit değildir. Bu yüzden çağrılar sabit IP'li
          bir aktarıcı ajan üzerinden gider.
          <p className="mt-2">
            Tesisin KBS arayüzünde kayıtlı IP olarak şunu tanımlaması gerekir:
          </p>
          <p className="mt-1 font-mono text-base font-bold">161.97.106.121</p>
        </Uyari>

        <p className="mt-4">
          <strong>Bildirim penceresi:</strong> giriş en fazla <strong>3 gün geriye</strong>, ileri tarihli
          rezervasyonlarda <strong>5 gün ileriye</strong> gönderilebilir. Bu aralığın dışındaki kayıtlar
          servise hiç gönderilmez, panelde açık hata olarak görünür.
        </p>
        <p className="mt-3">
          <strong>Tekrar deneme:</strong> dokümanın kuralı uygulanır — yalnızca <code>VTHatasi</code> (KBS
          veritabanı hatası) ve bilinmeyen hatalar yeniden denenir. <code>GirdiHatasi</code>,{' '}
          <code>YetkiHatasi</code> ve <code>KimlikDogrulamaHatasi</code> veri/yetki sorunudur; kayıt panelde
          düzeltilmeyi bekler, resmi servis boşuna meşgul edilmez.
        </p>
        <p className="mt-3">
          <strong>Düzeltme:</strong> hatalı giriş, KBS'nin <code>SEHVEN</code> çıkış türüyle iptal edilip doğru
          bilgiyle yeniden gönderilir.
        </p>
        <p className="mt-3">
          <strong>Uyruk:</strong> KBS'nin kendi ülke listesi kullanılır (244 değer, WSDL'den üretilmiştir).
          Karşılığı olmayan 8 uyruk için bildirim <em>gönderilmez</em> — yanlış ülke bildirmektense açık hata
          verilir.
        </p>
        <p className="mt-3">
          <strong>Manuel form</strong> seçeneği, web servis erişimi olmayan tesisler içindir: bildirim CSV
          olarak üretilir, resepsiyon panelden indirip KBS ekranına yükler. Kuyruk, tekrar deneme ve süre
          takibi her iki sağlayıcıda da aynıdır.
        </p>
        <p className="mt-3 text-slate-600">
          Kullanıcı bilgileri AES-256-GCM ile şifreli saklanır ve panelde bir daha görüntülenemez; yalnızca
          "tanımlı / tanımsız" bilgisi gösterilir.
        </p>
      </Bolum>

      <Bolum baslik="7. Sık karşılaşılan durumlar" rozet="Destek">
        <SSS soru="Tesis adresi açılmıyor, 'Tesis bulunamadı' diyor">
          Adres yanlış yazılmış olabilir; tesis listesindeki bağlantıyı kullanın. Kayıt varsa DNS joker kaydının
          proxied olduğunu doğrulayın.
        </SSS>
        <SSS soru="Otel 'oturumunuz başka bir tesise ait' hatası alıyor">
          Kullanıcı, başka bir otelin adresinde açık kalmış bir oturumla giriş yapmaya çalışıyor. Bu bilinçli bir
          korumadır; doğru adresten tekrar giriş yapması yeterli.
        </SSS>
        <SSS soru="Tesis yöneticisi parolasını unuttu">
          Tesis satırındaki <strong>Parola</strong> düğmesi yeni geçici parola üretir. İşlem denetim kaydına yazılır.
        </SSS>
        <SSS soru="Kimlik okuma çalışmıyor">
          Tesisin günlük kotası dolmuş olabilir (planına bakın). Kota doluysa resepsiyon bilgileri elle girmeye
          devam edebilir; check-in akışı durmaz.
        </SSS>
        <SSS soru="KBS bildirimi 'hata' durumunda kalmış">
          Genellikle zorunlu alan eksiktir (anne/baba adı gibi). Tesis kendi KBS panelinden "düzelt ve gönder"
          ile tamamlayabilir; sistem tekrar kuyruğa alır.
        </SSS>
      </Bolum>

      <Bolum baslik="8. Güvenlik notları" rozet="Önemli">
        <ul className="ml-5 list-disc space-y-2">
          <li>Platform paneli yalnızca <code>{platformDomain}/admin</code> üzerinde açılır; tesis adreslerinde yoktur.</li>
          <li>Platform oturumu ile otel oturumu ayrı token'lardır ve birbirinin yerine kullanılamaz.</li>
          <li>Bir otelin token'ı başka bir otelin adresinde reddedilir.</li>
          <li>Tesis açma, askıya alma ve parola sıfırlama işlemlerinin tamamı denetim kaydına yazılır.</li>
          <li>Geçici parolalar hiçbir yerde saklanmaz; yalnızca üretildiği anda gösterilir.</li>
        </ul>
      </Bolum>
    </div>
  );
}

function Bolum({ baslik, rozet, children }: { baslik: string; rozet?: string; children: React.ReactNode }) {
  return (
    <Kart>
      <KartBaslik>
        <h2 className="text-lg font-semibold">{baslik}</h2>
        {rozet && <Rozet>{rozet}</Rozet>}
      </KartBaslik>
      <KartGovde className="text-slate-700">{children}</KartGovde>
    </Kart>
  );
}

function Uyari({ children, renk = 'uyari' }: { children: React.ReactNode; renk?: 'uyari' | 'bilgi' }) {
  const sinif =
    renk === 'bilgi'
      ? 'border-marka-500/30 bg-marka-50 text-marka-700'
      : 'border-amber-300 bg-amber-50 text-amber-900';
  return <div className={`mt-4 rounded-xl border px-4 py-3 text-sm ${sinif}`}>{children}</div>;
}

function Kod({ children }: { children: string }) {
  return (
    <pre className="mt-3 overflow-x-auto rounded-xl bg-slate-900 p-4 text-sm text-slate-100">
      <code>{children}</code>
    </pre>
  );
}

function SSS({ soru, children }: { soru: string; children: React.ReactNode }) {
  return (
    <details className="border-b border-slate-100 py-3 last:border-0">
      <summary className="cursor-pointer font-medium text-slate-900">{soru}</summary>
      <p className="mt-2 text-sm text-slate-600">{children}</p>
    </details>
  );
}
